// Public /series directory reads for SSG prerender + the splash/show/episode loaders. Everything
// comes from ONE static JSON artifact on the Files CDN (services/routes/tts/directory.ts writes
// it on every public-tier privacy flip) — no API/Lambda/DB/WS at request time. The whole file
// is fetched once and sliced in memory, so prerender (Node) and a future client nav share one
// fetch. Node-prerender-safe: fetch only, no store/Amplify/@shared imports (see routes.ts).
// Files CDN base, per-stage (infra/gnothi/web.ts threads `routerFiles.url`): the old hardcoded prod host has no DNS record on staging, so the prerender's fetch died and took the whole build with it.
const FILES = import.meta.env.VITE_FILES_URL || 'https://files.gnothiai.com';
// LISTED_SHOWS_KEY, verbatim from services/routes/tts/directory.ts (C1).
export const LISTED_SHOWS_URL = `${FILES}/_system/system/listed_shows/listed-shows.json`;

export interface FeaturedShow {
  id: string; slug: string; title: string; owner_name: string;
  cover_url: string; episode_count: number; updated_at: string; category: string | null;
}
// No transcript field by design: audio.clean_txt is ALWAYS_PRIVATE, so directory.ts publishes no URL to it — don't re-add one, the corpus is raw user-fed source text.
export interface ShowEpisode {
  id: string; title: string; teaser: string | null; shownotes: string | null;
  audio_url: string | null; final_duration_seconds: number | null; created_at: string;
}
export interface ShowDetail {
  id: string; slug: string; title: string; description: string | null;
  user_id: string; image_alt: string | null; category: string | null;
  explicit: boolean; language: string | null; owner_name: string | null;
  subscribe_links: Array<{ platform: string; url: string }>;
  feed_url: string; cover_url: string;
  created_at: string; updated_at: string; privacy: string;
  episodes: ShowEpisode[];
}
interface ListedShows {
  generated_at: string; shows: FeaturedShow[]; pages?: string[]; ssg?: string[]; unlisted?: string[];
  bySlug: Record<string, ShowDetail>;
}

// Memoized: the prerender pass + every route loader share one Node process, so the artifact is
// fetched at most once per build. Evict on rejection so the first-build (no artifact yet, D1
// atom #5) failure doesn't wedge a permanently-rejected promise across callers.
let _artifact: Promise<ListedShows> | null = null;
function fetchListedShows(): Promise<ListedShows> {
  if (!_artifact) {
    _artifact = fetch(LISTED_SHOWS_URL).then(async (r) => {
      if (!r.ok) throw Object.assign(new Error(`HTTP ${r.status}`), { code: r.status });
      return (await r.json()) as ListedShows;
    });
    _artifact.catch(() => { _artifact = null; });
  }
  return _artifact;
}

export async function fetchFeaturedShows(limit = 12): Promise<FeaturedShow[]> {
  return (await fetchListedShows()).shows.filter((s) => s.slug).slice(0, limit);
}

export async function fetchShow(slug: string): Promise<ShowDetail | null> {
  return (await fetchListedShows()).bySlug[slug] ?? null;
}

export async function fetchEpisode(slug: string, id: string):
  Promise<{ show: ShowDetail; episode: ShowEpisode } | null> {
  const show = await fetchShow(slug);
  const episode = show?.episodes.find((e) => String(e.id) === String(id)) ?? null;
  return show && episode ? { show, episode } : null;
}

/** seconds → "m:ss" / "h:mm:ss" for episode-list + episode-detail display. */
export function formatDuration(secs: number | null): string | null {
  if (!secs) return null;
  const h = Math.floor(secs / 3600), m = Math.floor((secs % 3600) / 60), s = Math.floor(secs % 60);
  const mm = h ? String(m).padStart(2, '0') : String(m);
  return `${h ? `${h}:` : ''}${mm}:${String(s).padStart(2, '0')}`;
}

export interface PrerenderShow { slug: string; episodeIds: string[]; featured: boolean }

/** Every slug (featured + pages) that needs a prerendered show/episode page, with the episode ids react-router.config.ts needs to enumerate `/series/:slug/:episodeid` paths, and whether it's featured (sitemap-eligible) vs plain `pages` (prerendered + noindex, but not sitemap-listed). */
export async function fetchPrerenderShows(): Promise<PrerenderShow[]> {
  const data = await fetchListedShows();
  // `podcasts.slug` is nullable and directory.ts slug-filters `bySlug`/`pages` but NOT `shows`, so a slugless featured row would prerender `/series/null` and put that URL in the sitemap.
  const featuredSlugs = new Set(data.shows.filter((s) => s.slug).map((s) => s.slug));
  // READ-SIDE SHIM (writer is `pages`-only): accepts a stale PRE-rename artifact from either earlier vocabulary, since nothing guarantees the cutover deploy regenerates it before this build reads it — and the `?? []` would swallow the miss as "no shows to prerender", silently shipping zero pages for that tier with no error. Delete once a post-cutover deploy has written a `pages` artifact.
  const slugs = [...new Set([...featuredSlugs, ...(data.pages ?? data.ssg ?? data.unlisted ?? [])])];
  return slugs.map((slug) => ({
    slug, featured: featuredSlugs.has(slug),
    episodeIds: (data.bySlug[slug]?.episodes ?? []).map((e) => String(e.id)),
  }));
}
